import React, { useState, useEffect } from "react";
import { TextField, Checkbox, FormControlLabel } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/styles";
import moment from "moment";
import styled from "styled-components";
import RemoveButton from "../../../../../app/components/buttons/remove";
import ToolTip from "../../../../../app/components/tooltip";
import { useStudyPlan } from "../../../../../app/contexts/study.context";
import { UPDATE_COUNTRIES_TRACEABILITY } from "../../../../../app/contexts/constants/study.context.constants";
import { isRemodelStudyPlanType } from "../../../utils/helpers";

const TOTAL_COLUMNS = 11;
const MILLISECONDS_CONSTANT = 24 * 60 * 60 * 1000;
const DATE_FORMAT = "DD/MMM/YYYY";
const SITES_TO_ACTIVATE = "SITES_TO_ACTIVATE";
const PROJECT_USING_PP_LABEL = "Projected using PP";

const ActivationProfilesTable = ({
  activationProfiles,
  FSIV,
  siteGroupId,
  countryId,
  siteGroup,
}) => {
  const inputClasses = useInputStyles();
  const [sitesBoundValue, setSitesBoundValue] = useState(null);
  const [tempValues, setTempValues] = useState({});

  const {
    studyPlan,
    STUDY_PLAN_TYPES,
    updateCountries,
    isEditingAPTableEnabled,
    setIsEditingAPTableEnabled,
  } = useStudyPlan();
  const { SCENARIO_B } = STUDY_PLAN_TYPES;
  const { studyPlanType, studyPlanTypeDetail } = studyPlan || {};
  const isRemodelType = isRemodelStudyPlanType(studyPlanTypeDetail);

  // Helper function to update the last profile with a value and apply changes
  const updateLastProfileAndApply = (profiles, newSitesBoundValue = sitesBoundValue) => {
    if (newSitesBoundValue === null || profiles.length === 0) {
      return profiles;
    }

    let updatedProfiles = [...profiles];
    let lastProfileIndex = -1;
    
    // Find the last profile that has a siteActivationProfilePct value
    for (let i = updatedProfiles.length - 1; i >= 0; i--) {
      const profile = updatedProfiles[i];
      if (profile?.siteActivationProfilePct !== null && profile?.siteActivationProfilePct !== undefined) {
        lastProfileIndex = i;
        break;
      }
    }
    
    // Check if the last profile's label is one that should not be modified
    const NOT_ALLOWED_LABELS = ["Activated", "Projected in Remarque"];
    const lastProfileLabel = updatedProfiles[lastProfileIndex]?.siteActivationLabel;
    
    // Only update if the label is NOT in the NOT_ALLOWED_LABELS list
    if (lastProfileIndex !== -1 && !NOT_ALLOWED_LABELS.includes(lastProfileLabel)) {
      updatedProfiles[lastProfileIndex] = {
        ...updatedProfiles[lastProfileIndex],
        siteActivationProfilePct: newSitesBoundValue
      };
      return updatedProfiles;
    }
    
    // If we can't update the last profile (because it's protected), return original profiles
    return profiles;
  };

  // Helper function to filter and format activation profiles for update
  const prepareActivationProfilesForUpdate = (profiles, overrideFlag) => {
    return profiles.reduce((acc, profile) => {
      // Only include profiles that meet the filter criteria
      if (profile.siteActivationProfilePct !== null ||
          profile.siteActivationProfileDays !== null ||
          (typeof profile.siteActivationProfileId === "string" &&
           !profile.siteActivationProfileId.startsWith("empty-"))) {
        acc.push({
          ...profile,
          overrideFl: overrideFlag ? 1 : 0,
        });
      }
      return acc;
    }, []);
  };

  const handleChange = (value, index, property) => {
    if (!isEditingAPTableEnabled) return;
    
    // Store temporary values without updating the backend
    const key = `${index}-${property}`;
    setTempValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleBlur = (value, index, property) => {
    if (!isEditingAPTableEnabled) return;
    
    // Apply changes only when user finishes editing (onBlur)
    applyChange(value, index, property);
    
    // Clear temporary value
    const key = `${index}-${property}`;
    setTempValues(prev => {
      const newTempValues = { ...prev };
      delete newTempValues[key];
      return newTempValues;
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.target.blur(); // Trigger blur manually to save changes
    }
  };

  // Helper function to get the display value (temporary or actual)
  const getDisplayValue = (profile, index, property) => {
    const key = `${index}-${property}`;
    if (tempValues[key] !== undefined) {
      return tempValues[key];
    }
    return profile[property] ?? "";
  };

  const applyChange = (value, index, property) => {
    const newActivationProfiles = [...activationProfiles];
    if (property === "siteActivationProfilePct") {
      // Format to 2 decimal places
      let formattedValue = value === "" ? null : parseFloat(parseFloat(value).toFixed(2));
      newActivationProfiles[index][property] = formattedValue;
    } else if (property === "siteActivationProfileDays") {
      newActivationProfiles[index][property] = value === "" ? null : parseInt(value, 10);
    } else {
      newActivationProfiles[index][property] = value;
    }

    // Set label to "Projected using PP" when user starts typing in an empty column
    if (value !== "" && newActivationProfiles[index].siteActivationLabel === "") {
      newActivationProfiles[index].siteActivationLabel = PROJECT_USING_PP_LABEL;
      newActivationProfiles[index].siteActivationProfilePct = sitesBoundValue;
    }

    // Clear label when both percentage and day are empty
    if (newActivationProfiles[index].siteActivationProfilePct === null &&
      newActivationProfiles[index].siteActivationProfileDays === null
    ) {
      newActivationProfiles[index].siteActivationLabel = "";
    }

    // Remove the 'empty-' prefix from the ID when a value is added
    const profileId = newActivationProfiles[index].siteActivationProfileId;
    if (value !== "" && typeof profileId === "string" && profileId.startsWith("empty-")) {
      newActivationProfiles[index].siteActivationProfileId = profileId.replace(
        "empty-",
        "",
      );
    }

    const update = {
      activationProfiles: prepareActivationProfilesForUpdate(newActivationProfiles, isEditingAPTableEnabled),
    };
    updateCountries(countryId, siteGroupId, update, false, UPDATE_COUNTRIES_TRACEABILITY.ACTIVATION_PROFILES);
  };

  const handleDelete = (profileId) => {
    if (!isEditingAPTableEnabled) return;
    
    // Filter out the deleted profile
    const filteredProfiles = activationProfiles.filter(
      (profile) => profile.siteActivationProfileId !== profileId,
    );
    
    // Update the last profile with sitesBoundValue and prepare for update
    const updatedProfiles = updateLastProfileAndApply(filteredProfiles);
    const update = {
      activationProfiles: prepareActivationProfilesForUpdate(updatedProfiles, isEditingAPTableEnabled),
    };
    updateCountries(countryId, siteGroupId, update, false, UPDATE_COUNTRIES_TRACEABILITY.ACTIVATION_PROFILES);
  };

  const handleOverrideCheckboxChange = (checked) => {
    // Don't allow any changes if it's a Remodel type
    if (isRemodelType) return;
    
    setIsEditingAPTableEnabled(checked);
    const update = {
      activationProfiles: prepareActivationProfilesForUpdate(activationProfiles, checked),
    };
    updateCountries(countryId, siteGroupId, update, false, UPDATE_COUNTRIES_TRACEABILITY.ACTIVATION_PROFILES);
  };

  // Helper function to determine if a profile should be editable
  const isProfileEditable = (profile, index) => {
    if (!isEditingAPTableEnabled) return false;
    
    // Find the last profile that has a siteActivationProfilePct value
    let lastProfileWithValueIndex = -1;
    for (let i = activationProfiles.length - 1; i >= 0; i--) {
      const p = activationProfiles[i];
      if (p?.siteActivationProfilePct !== null && p?.siteActivationProfilePct !== undefined) {
        lastProfileWithValueIndex = i;
        break;
      }
    }
    
    // Only the last profile with a value should be disabled (not editable)
    if (lastProfileWithValueIndex !== -1 && index === lastProfileWithValueIndex) {
      return false;
    }
    
    return true;
  };

  const validateProfileWithValue = () => {
    const hasProfileWithValue = activationProfiles.some(profile => 
      profile?.siteActivationProfilePct !== null && profile?.siteActivationProfilePct !== undefined
    );
    return hasProfileWithValue;
  }

  // First useEffect: Only calculate and set sitesBoundValue based on parameters
  useEffect(() => {
    const parameters = siteGroup?.parameters;
    if (parameters) {
      const sitesToActivate = parameters.find(p => p.parameterName === SITES_TO_ACTIVATE);
      if (sitesToActivate) {
        const selectedBound = studyPlanType === SCENARIO_B
          ? sitesToActivate?.lowerBound
          : sitesToActivate?.upperBound;
        setSitesBoundValue(selectedBound);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteGroup?.parameters]);

  useEffect(() => {
      const handleSave = async () => {
        if (sitesBoundValue !== null && activationProfiles.length > 0 && isEditingAPTableEnabled) {
          const hasProfileWithValue = validateProfileWithValue();
          if (hasProfileWithValue) {
            const updatedProfiles = updateLastProfileAndApply(activationProfiles, sitesBoundValue);
            
            // Only update if the profiles actually changed
            const hasRealChange = JSON.stringify(updatedProfiles) !== JSON.stringify(activationProfiles);
            if (!hasRealChange) return;
            
            const update = {
              activationProfiles: prepareActivationProfilesForUpdate(updatedProfiles, isEditingAPTableEnabled),
            };
            updateCountries(countryId, siteGroupId, update, false, UPDATE_COUNTRIES_TRACEABILITY.ACTIVATION_PROFILES);
          }
        }
      };

      handleSave();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sitesBoundValue, isEditingAPTableEnabled]);

  // Sync isEditingAPTableEnabled when activationProfiles change
  useEffect(() => {
    const hasProfileWithValue = validateProfileWithValue();
    if (hasProfileWithValue) {
      setIsEditingAPTableEnabled(activationProfiles?.[0]?.overrideFl === 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activationProfiles]);

  return (
    <>
      <div style={{ marginTop: "20px", marginBottom: "20px", display: "flex", alignItems: "center" }}>
        <FormControlLabel
          control={
            <Checkbox
              style={{
                color: isRemodelType ? "#999" : "var(--teal-600)",
                opacity: isRemodelType ? 0.6 : 1,
              }}
              checked={isEditingAPTableEnabled}
              onChange={(e) => handleOverrideCheckboxChange(e.target.checked)}
              color="primary"
              disabled={isRemodelType}
            />
          }
          label={
            <strong style={{ 
              color: isRemodelType ? "#999" : "inherit",
              opacity: isRemodelType ? 0.6 : 1 
            }}>
              Override
            </strong>
          }
          style={{
            opacity: isRemodelType ? 0.6 : 1,
          }}
        />
        <ToolTip 
          info={
            isRemodelType 
              ? "Override is always enabled for Remodel studies. Activation profiles cannot be reset to calculated values."
              : "Check to confirm that calculated data will be overwritten"
          } 
        />
      </div>
      <Table>
        <Row>
          <Cell variant="header">
            <Typography align="right">
              <strong>Label</strong>
            </Typography>
          </Cell>
          {Array.from({ length: TOTAL_COLUMNS }, (_, index) => (
            <Cell key={`label-${index}`}>
              <Typography>
                {activationProfiles[index]?.siteActivationLabel || ""}
              </Typography>
            </Cell>
          ))}
        </Row>
        <Row>
          <Cell variant="header">
            <Typography align="right">
              <strong># of Sites: </strong>
            </Typography>
          </Cell>
          {Array.from({ length: TOTAL_COLUMNS }, (_, columnIndex) => {
            const profile = activationProfiles[columnIndex];
            return (
              <Cell key={`sites-col-${columnIndex}`}>
                {profile ? (
                  <TextField
                    id={`sites-input-${columnIndex}-${siteGroupId}-${countryId}`}
                    key={`sites-input-${profile.siteActivationProfileId || columnIndex}`}
                    style={{ width: "80%" }}
                    type="number"
                    inputProps={{
                      min: "0",
                      step: "1",
                      onInput: (e) => {
                        let value = e.target.value;
                        value = value.replace(/[.-]/g, '');
                        value = value.replace(/^0+/, '') || '0';
                        e.target.value = value;
                      },
                      onKeyDown: (e) => {
                        if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E') {
                          e.preventDefault();
                        }
                      }
                    }}
                    classes={inputClasses}
                    value={getDisplayValue(profile, columnIndex, "siteActivationProfilePct")}
                    onChange={(event) =>
                      handleChange(
                        event.target.value,
                        columnIndex,
                        "siteActivationProfilePct",
                      )
                    }
                    onBlur={(event) =>
                      handleBlur(
                        event.target.value,
                        columnIndex,
                        "siteActivationProfilePct",
                      )
                    }
                    onKeyDown={(event) => handleKeyDown(event)}
                    variant="outlined"
                    size="small"
                    disabled={!isProfileEditable(profile, columnIndex)}
                  />
                ) : (
                  // Empty cell for columns beyond available profiles
                  <div key={`sites-empty-${columnIndex}`} style={{ width: "80%", height: "40px" }}></div>
                )}
              </Cell>
            );
          })}
        </Row>
        <Row>
          <Cell variant="header">
            <Typography align="right">
              <strong>Days</strong>
            </Typography>
          </Cell>
          {Array.from({ length: TOTAL_COLUMNS }, (_, columnIndex) => {
            const profile = activationProfiles[columnIndex];
            return (
              <Cell key={`days-col-${columnIndex}`}>
                {profile ? (
                  <TextField
                    id={`days-input-${columnIndex}-${siteGroupId}-${countryId}`}
                    key={`days-input-${profile.siteActivationProfileId || columnIndex}`}
                    style={{ width: "80%" }}
                    type="number"
                    classes={inputClasses}
                    value={getDisplayValue(profile, columnIndex, "siteActivationProfileDays")}
                    onChange={(event) =>
                      handleChange(
                        event.target.value,
                        columnIndex,
                        "siteActivationProfileDays",
                      )
                    }
                    onBlur={(event) =>
                      handleBlur(
                        event.target.value,
                        columnIndex,
                        "siteActivationProfileDays",
                      )
                    }
                    onKeyDown={(event) => handleKeyDown(event)}
                    variant="outlined"
                    size="small"
                    disabled={!isEditingAPTableEnabled}
                  />
                ) : (
                  // Empty cell for columns beyond available profiles
                  <div key={`days-empty-${columnIndex}`} style={{ width: "80%", height: "40px" }}></div>
                )}
              </Cell>
            );
          })}
        </Row>
        <Row>
          <Cell variant="header">
            <Typography align="right">
              <strong>Date</strong>
            </Typography>
          </Cell>
          {Array.from({ length: TOTAL_COLUMNS }, (_, columnIndex) => {
            const profile = activationProfiles[columnIndex];
            const days = profile?.siteActivationProfileDays;
            const date = profile && FSIV?.milestoneDateMillis && days !== null && days !== undefined
              ? moment(FSIV.milestoneDateMillis + days * MILLISECONDS_CONSTANT)
                .utc()
                .format(DATE_FORMAT)
              : "";
            return (
              <Cell key={`date-col-${columnIndex}`}>
                <Typography>
                  {date}
                </Typography>
              </Cell>
            );
          })}
        </Row>
        <Row>
          <Cell variant="header">
            <Typography align="right">
              <strong>Delete</strong>
            </Typography>
          </Cell>
          {Array.from({ length: TOTAL_COLUMNS }, (_, columnIndex) => {
            const profile = activationProfiles[columnIndex];
            const days = profile?.siteActivationProfileDays;
            const showDeleteButton = profile && days !== null && days !== undefined && isEditingAPTableEnabled;
            return (
              <Cell key={`delete-col-${columnIndex}`}>
                {showDeleteButton ? (
                  <RemoveButton
                    key={`delete-btn-${profile.siteActivationProfileId}`}
                    onClick={() => handleDelete(profile.siteActivationProfileId)}
                  />
                ) : (
                  ""
                )}
              </Cell>
            );
          })}
        </Row>
      </Table>
    </>
  );
};

const useInputStyles = makeStyles(() => ({
  root: {
    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      display: "none",
    },
    "& input[type=number]": {
      textAlign: "center",
      "-moz-appearance": "textfield",
    },
  },
}));

const Table = styled.div`
  margin-top: 20px;
  display: grid;
  width: 100%;
  grid-template-rows: auto auto;
  border: 1px solid var(--surface-300);
  border-radius: 4px;
  border-right: none;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  height: 50px;
  border-bottom: 1px solid var(--surface-300);
  &:last-child {
    border: none;
  }
`;

const Cell = styled.div`
  background: ${(p) =>
    p.variant === "header" ? "var(--surface-50)" : "transparent"};
  height: 100%;
  min-width: 0;
  align-items: center;
  justify-content: center;
  display: flex;
  border-right: 1px solid var(--surface-300);

  &:first-child {
    justify-content: flex-end;
    padding-right: 1em;
  }
`;

export default ActivationProfilesTable;